import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useRouteError } from 'react-router-dom';
import { ErrorScreen } from '@/components/ErrorBoundary/ErrorScreen';
import { Sentry } from '@/lib/sentry';
import { Box } from '@/ui';
import { isChunkLoadError } from './isChunkLoadError';

export const RouteErrorBoundary = () => {
  const error = useRouteError();
  const { t } = useTranslation('common');
  const isChunkError = isChunkLoadError(error);

  useEffect(() => {
    // Chunk-load failures are recovered by reloading and are intentionally not
    // reported. Everything else is a real render error worth capturing — the
    // app's class ErrorBoundary never sees route-render errors because
    // RouterProvider catches them first.
    if (!isChunkError) {
      Sentry.captureException(error, { tags: { runtime: true } });
    }
  }, [error, isChunkError]);

  const reload = () => window.location.reload();

  if (isChunkError) {
    return (
      <ErrorScreen
        reloadLabel={t('error_boundary.reload')}
        title={t('error_boundary.chunk_title')}
        message={
          <Trans
            components={{ nowrap: <Box as="span" whiteSpace="nowrap" /> }}
            i18nKey="error_boundary.chunk_message"
            t={t}
          />
        }
        onReload={reload}
      />
    );
  }

  return (
    <ErrorScreen
      message={t('error_boundary.message')}
      reloadLabel={t('error_boundary.reload')}
      title={t('error_boundary.title')}
      onReload={reload}
    />
  );
};
